// src/pages/ReviewAfterExperience.tsx
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaCommentDots,
  FaEnvelope,
  FaExclamationTriangle,
  FaGift,
  FaHeart,
  FaLock,
  FaMagic,
  FaMapMarkedAlt,
  FaPaperPlane,
  FaQrcode,
  FaRegStar,
  FaShieldAlt,
  FaSpinner,
  FaStar,
  FaStore,
  FaUserCircle,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { supabase } from '../services/supabaseClient';

const ratingLabels = {
  fr: ['À améliorer', 'Correct', 'Bien', 'Très bien', 'Exceptionnel'],
  en: ['Needs work', 'Fair', 'Good', 'Very good', 'Exceptional'],
};

const copy = {
  fr: {
    seoTitle: 'Avis après expérience | Forfeo Explore',
    seoDescription: 'Partagez votre avis après une expérience Forfeo Explore et aidez les voyageurs comme les partenaires locaux à s’améliorer.',
    badge: 'Avis après expérience',
    title: 'Votre moment compte. Votre avis aussi.',
    subtitle:
      'Après une réservation, un Last Call, un voucher ou une expérience locale, partagez votre ressenti. Forfeo utilise ces avis pour améliorer la qualité, aider les partenaires et guider les prochains voyageurs.',
    codeLabel: 'Code voucher ou réservation',
    codePlaceholder: 'Ex. FE-ABC1234567 ou ID réservation',
    emailLabel: 'Courriel utilisé lors de l’achat',
    emailPlaceholder: 'votre@email.com',
    nameLabel: 'Nom complet',
    namePlaceholder: 'Votre nom',
    sourceLabel: 'Type d’expérience',
    sourceOptions: {
      voucher: 'Voucher / billet Forfeo',
      reservation: 'Réservation Last Call',
      gift: 'Carte ou cadeau Forfeo',
      other: 'Autre expérience',
    },
    ratingTitle: 'Quelle note donnez-vous à votre expérience ?',
    titleLabel: 'Titre de votre avis',
    titlePlaceholder: 'Ex. Super belle découverte locale',
    commentLabel: 'Votre commentaire',
    commentPlaceholder:
      'Parlez de l’accueil, de la qualité, de l’ambiance, de la facilité d’utilisation du voucher ou de ce qui pourrait être amélioré.',
    publicLabel: 'J’autorise Forfeo à utiliser mon avis de façon publique après modération.',
    submit: 'Envoyer mon avis',
    submitting: 'Envoi en cours...',
    successTitle: 'Merci pour votre avis !',
    successText:
      'Votre retour a été reçu. L’équipe Forfeo pourra l’utiliser pour améliorer l’expérience et accompagner le partenaire concerné.',
    errorTitle: 'Impossible d’envoyer l’avis',
    required: 'Veuillez remplir les champs obligatoires et choisir une note.',
    privacy: 'Votre avis est enregistré de façon sécurisée. Les avis publics sont modérés avant diffusion.',
    ctaPurchases: 'Voir mes achats',
    ctaReservations: 'Voir mes réservations',
    ctaGift: 'Offrir un moment',
    howTitle: 'Comment Forfeo utilise les avis',
    how: [
      'Identifier les meilleures expériences locales.',
      'Améliorer les fiches, les vouchers et les confirmations.',
      'Aider les partenaires à corriger rapidement les irritants.',
      'Créer une preuve de confiance pour les prochains voyageurs.',
    ],
    trustTitle: 'Un avis utile, pas seulement une étoile',
    trustText:
      'Les commentaires détaillés aident Forfeo à comprendre ce qui a vraiment été vécu : accueil, qualité, réservation, clarté du billet, prix, ambiance et valeur perçue.',
  },
  en: {
    seoTitle: 'Post-experience review | Forfeo Explore',
    seoDescription: 'Share your review after a Forfeo Explore experience and help travelers and local partners improve.',
    badge: 'Post-experience review',
    title: 'Your moment matters. Your review does too.',
    subtitle:
      'After a reservation, Last Call, voucher or local experience, share how it went. Forfeo uses reviews to improve quality, support partners and guide future travelers.',
    codeLabel: 'Voucher or reservation code',
    codePlaceholder: 'Ex. FE-ABC1234567 or reservation ID',
    emailLabel: 'Email used for the purchase',
    emailPlaceholder: 'your@email.com',
    nameLabel: 'Full name',
    namePlaceholder: 'Your name',
    sourceLabel: 'Experience type',
    sourceOptions: {
      voucher: 'Forfeo voucher / ticket',
      reservation: 'Last Call reservation',
      gift: 'Forfeo card or gift',
      other: 'Other experience',
    },
    ratingTitle: 'How would you rate your experience?',
    titleLabel: 'Review title',
    titlePlaceholder: 'Ex. A great local discovery',
    commentLabel: 'Your comment',
    commentPlaceholder:
      'Tell us about the welcome, quality, atmosphere, voucher experience or what could be improved.',
    publicLabel: 'I allow Forfeo to use my review publicly after moderation.',
    submit: 'Submit my review',
    submitting: 'Submitting...',
    successTitle: 'Thank you for your review!',
    successText:
      'Your feedback was received. The Forfeo team may use it to improve the experience and support the partner involved.',
    errorTitle: 'Unable to submit review',
    required: 'Please complete the required fields and select a rating.',
    privacy: 'Your review is stored securely. Public reviews are moderated before publication.',
    ctaPurchases: 'View my purchases',
    ctaReservations: 'View my reservations',
    ctaGift: 'Gift a moment',
    howTitle: 'How Forfeo uses reviews',
    how: [
      'Identify the best local experiences.',
      'Improve listings, vouchers and confirmations.',
      'Help partners quickly fix friction points.',
      'Build trust for future travelers.',
    ],
    trustTitle: 'A useful review, not just a star',
    trustText:
      'Detailed comments help Forfeo understand what really happened: welcome, quality, booking, ticket clarity, price, atmosphere and perceived value.',
  },
};

type ReviewSource = 'voucher' | 'reservation' | 'gift' | 'other';

const normalizeCodeFromUrl = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get('code') || params.get('voucher') || params.get('reservation') || '';
};

const reviewErrorMessage = (error: unknown, lang: 'fr' | 'en') => {
  const code = String(error || '').trim();
  const messages: Record<string, { fr: string; en: string }> = {
    invalid_email: {
      fr: 'Veuillez entrer une adresse courriel valide.',
      en: 'Please enter a valid email address.',
    },
    rating_required: {
      fr: 'Veuillez sélectionner une note de 1 à 5 étoiles.',
      en: 'Please select a 1 to 5 star rating.',
    },
    comment_too_short: {
      fr: 'Ajoutez un court commentaire avant de soumettre votre avis.',
      en: 'Please add a short comment before submitting your review.',
    },
    invalid_json: {
      fr: 'La demande envoyée au serveur est invalide. Rechargez la page et réessayez.',
      en: 'The request sent to the server is invalid. Please reload the page and try again.',
    },
    insert_failed: {
      fr: 'Impossible d’enregistrer l’avis pour le moment. Vérifiez que la migration experience_reviews est bien appliquée.',
      en: 'Unable to save the review right now. Please verify that the experience_reviews migration is applied.',
    },
    server_not_configured: {
      fr: 'La fonction avis n’est pas encore correctement configurée côté Supabase.',
      en: 'The review function is not correctly configured in Supabase yet.',
    },
  };
  return messages[code]?.[lang] || (lang === 'en' ? 'Unable to submit your review right now.' : 'Impossible de soumettre votre avis pour le moment.');
};

export default function ReviewAfterExperience() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');
  const lang = isEn ? 'en' : 'fr';
  const t = copy[lang];

  const [sourceType, setSourceType] = useState<ReviewSource>('voucher');
  const [code, setCode] = useState(normalizeCodeFromUrl(location.search));
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewTitle, setReviewTitle] = useState('');
  const [comment, setComment] = useState('');
  const [allowPublic, setAllowPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const selectedRatingLabel = useMemo(() => {
    const value = hoverRating || rating;
    if (!value) return '';
    return ratingLabels[lang][value - 1];
  }, [hoverRating, rating, lang]);

  const submitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!email.trim() || !rating || !comment.trim()) {
      setMessage({ type: 'error', text: t.required });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-experience-review', {
        body: {
          lang,
          source_type: sourceType,
          code: code.trim(),
          customer_email: email.trim(),
          customer_name: name.trim(),
          rating,
          review_title: reviewTitle.trim(),
          comment: comment.trim(),
          is_public: allowPublic,
          page_url: typeof window !== 'undefined' ? window.location.href : null,
        },
      });

      if (error) {
        console.error('submit-experience-review invoke error', error);
        throw new Error(t.errorTitle);
      }

      if (!data?.ok) {
        const serverError = data?.error || 'unexpected_error';
        const serverDetails = typeof data?.details === 'string' ? data.details : '';
        console.warn('submit-experience-review rejected', data);
        throw new Error(serverDetails || reviewErrorMessage(serverError, lang));
      }

      setMessage({ type: 'success', text: t.successText });
      setCode('');
      setEmail('');
      setName('');
      setRating(0);
      setHoverRating(0);
      setReviewTitle('');
      setComment('');
      setAllowPublic(false);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || t.errorTitle });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans overflow-hidden">
      <Header />
      <SeoHead title={t.seoTitle} description={t.seoDescription} canonicalPath="/avis-experience" />

      <section className="relative pt-28 pb-16 bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] [background-size:34px_34px]" />
        <div className="absolute -top-24 right-0 h-80 w-80 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="relative container mx-auto px-4">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-200 backdrop-blur-md">
                <FaCommentDots /> {t.badge}
              </div>
              <h1 className="mt-6 max-w-4xl text-4xl md:text-6xl font-black leading-[0.95] tracking-tighter">
                {t.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-slate-300 font-medium">
                {t.subtitle}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button onClick={() => navigate('/mes-achats')} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-950 shadow-xl hover:bg-cyan-50 transition">
                  <FaQrcode /> {t.ctaPurchases}
                </button>
                <button onClick={() => navigate('/mes-reservations')} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-6 py-4 text-xs font-black uppercase tracking-widest text-white backdrop-blur-md hover:bg-white/15 transition">
                  <FaMapMarkedAlt /> {t.ctaReservations}
                </button>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.65, delay: 0.1 }} className="relative">
              <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-cyan-400/25 to-fuchsia-500/25 blur-2xl" />
              <div className="relative rounded-[3rem] border border-white/15 bg-white/10 p-6 md:p-8 backdrop-blur-2xl shadow-2xl">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-white text-blue-600 shadow-xl">
                    <FaStar size={28} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-200">Forfeo quality loop</p>
                    <h2 className="text-2xl font-black tracking-tight text-white">{t.trustTitle}</h2>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-slate-300 font-semibold">{t.trustText}</p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[FaShieldAlt, FaStore, FaHeart, FaMagic].map((Icon, index) => (
                    <div key={index} className="rounded-2xl border border-white/10 bg-white/10 p-4 text-center">
                      <Icon className="mx-auto mb-2 text-cyan-200" />
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-300">{['Qualité', 'Partenaire', 'Moment', 'Confiance'][index]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 grid lg:grid-cols-[1fr_0.72fr] gap-8 items-start">
          <motion.form onSubmit={submitReview} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-[2.5rem] border border-slate-200 bg-white p-6 md:p-8 shadow-xl">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><FaPaperPlane /></div>
              <div>
                <h2 className="text-2xl font-black tracking-tight text-slate-900">{t.badge}</h2>
                <p className="text-sm font-semibold text-slate-500">{t.privacy}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.sourceLabel}</span>
                <select value={sourceType} onChange={(e) => setSourceType(e.target.value as ReviewSource)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-slate-800 outline-none focus:border-blue-500">
                  <option value="voucher">{t.sourceOptions.voucher}</option>
                  <option value="reservation">{t.sourceOptions.reservation}</option>
                  <option value="gift">{t.sourceOptions.gift}</option>
                  <option value="other">{t.sourceOptions.other}</option>
                </select>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.codeLabel}</span>
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t.codePlaceholder} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-slate-800 outline-none focus:border-blue-500" />
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.emailLabel} *</span>
                <div className="relative">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.emailPlaceholder} className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 font-bold text-slate-800 outline-none focus:border-blue-500" />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.nameLabel}</span>
                <div className="relative">
                  <FaUserCircle className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.namePlaceholder} className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 font-bold text-slate-800 outline-none focus:border-blue-500" />
                </div>
              </label>
            </div>

            <div className="mt-7 rounded-3xl border border-blue-100 bg-blue-50/60 p-5">
              <p className="mb-3 text-sm font-black text-slate-900">{t.ratingTitle} *</p>
              <div className="flex flex-wrap items-center gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button key={value} type="button" onMouseEnter={() => setHoverRating(value)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(value)} className="text-3xl transition hover:scale-110" aria-label={`${value} stars`}>
                    {(hoverRating || rating) >= value ? <FaStar className="text-amber-400 drop-shadow" /> : <FaRegStar className="text-slate-300" />}
                  </button>
                ))}
                <span className="ml-2 rounded-full bg-white px-3 py-1 text-xs font-black uppercase tracking-widest text-slate-600">{selectedRatingLabel || '—'}</span>
              </div>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.titleLabel}</span>
              <input value={reviewTitle} onChange={(e) => setReviewTitle(e.target.value)} placeholder={t.titlePlaceholder} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-slate-800 outline-none focus:border-blue-500" />
            </label>

            <label className="mt-5 block">
              <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.commentLabel} *</span>
              <textarea required rows={6} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t.commentPlaceholder} className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-semibold text-slate-800 outline-none focus:border-blue-500" />
            </label>

            <label className="mt-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-600">
              <input type="checkbox" checked={allowPublic} onChange={(e) => setAllowPublic(e.target.checked)} className="mt-1 h-4 w-4 accent-blue-600" />
              <span>{t.publicLabel}</span>
            </label>

            <AnimatePresence>
              {message && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className={`mt-5 rounded-2xl p-4 text-sm font-bold ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                  {message.type === 'success' ? <FaCheckCircle className="inline mr-2" /> : <FaExclamationTriangle className="inline mr-2" />}
                  {message.type === 'success' ? t.successTitle + ' ' : ''}{message.text}
                </motion.div>
              )}
            </AnimatePresence>

            <button disabled={loading} type="submit" className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-slate-950 px-6 py-4 text-xs font-black uppercase tracking-widest text-white shadow-xl transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {loading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              {loading ? t.submitting : t.submit}
            </button>
          </motion.form>

          <aside className="space-y-6">
            <div className="rounded-[2.5rem] border border-slate-200 bg-white p-6 md:p-8 shadow-xl">
              <h3 className="flex items-center gap-3 text-2xl font-black tracking-tight text-slate-900"><FaShieldAlt className="text-blue-600" /> {t.howTitle}</h3>
              <div className="mt-5 space-y-3">
                {t.how.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                    <FaCheckCircle className="mt-1 text-emerald-500" />
                    <p className="text-sm font-semibold leading-relaxed text-slate-600">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2.5rem] bg-gradient-to-br from-slate-950 to-blue-950 p-6 md:p-8 text-white shadow-2xl">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-cyan-200"><FaGift /></div>
              <h3 className="mt-5 text-2xl font-black tracking-tight">{isEn ? 'Want to gift another moment?' : 'Envie d’offrir un autre moment ?'}</h3>
              <p className="mt-3 text-sm font-semibold leading-relaxed text-slate-300">
                {isEn
                  ? 'Use Forfeo Explore to find ideas, then buy the official virtual card on forfeo.com when you are ready.'
                  : 'Utilisez Forfeo Explore pour trouver des idées, puis achetez la carte virtuelle officielle sur forfeo.com quand vous êtes prêt.'}
              </p>
              <Link to="/offrir-un-moment" className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-950 hover:bg-cyan-50 transition">
                {t.ctaGift} <FaArrowRight />
              </Link>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 text-sm font-semibold text-slate-600">
              <FaLock className="inline mr-2 text-slate-500" /> {t.privacy}
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
